import React, { useEffect, useState } from 'react';
import Homepage from './components/homePage';
import { Routes, Route, Navigate } from "react-router-dom";
import RegisterPage from './components/registerpage';
import Survey from './components/survey';
import AdminRoute from './components/AdminRoute';
import Footer from './components/footer';
import Navbar2 from './components/navbar2';
import './App.css';
import { useDispatch } from 'react-redux';
import { fetchBlastOffRuleResult } from './actions';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import PasswordReset from './components/PasswordReset';
import ForgotPassword from './components/ForgotPassword';
import Snackbar from './components/Snackbar';
import Loading from './components/loading';
import { SESSION_EXPIRED_EVENT } from './service/apiClient';

const LoginSignUpRoute = React.lazy(() => import('./components/login'));

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'info' });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const loginType = localStorage.getItem("loginType");

  // Fetch initial data only when user is logged in and has a token
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && isLoggedIn) {
      dispatch(fetchBlastOffRuleResult());
    }
  }, [dispatch, isLoggedIn]);

  // Check login state on mount
  useEffect(() => {
    const token = localStorage.getItem("userInfo");
    setIsLoggedIn(!!token);
  }, []);

  // Update login state if MSAL authentication changes
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      localStorage.setItem("userInfo", JSON.stringify(accounts[0]));
      localStorage.setItem("loginType", "microsoft");
      setIsLoggedIn(true);
    }
  }, [isAuthenticated, accounts]);

  // Listen for global session-expired events from apiClient
  useEffect(() => {
    const handleSessionExpired = () => {
      setSnackbar({ open: true, message: 'Your session has expired. Redirecting to login...', type: 'error' });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  return (
    <div className="App">
      <Snackbar message={snackbar.message} type={snackbar.type} open={snackbar.open} onClose={closeSnackbar} duration={0} />
      <Navbar2 />
      <main className="main-content">
        <Routes>
          {/* Redirect root based on login state */}
          <Route path="/" element={
            isLoggedIn && loginType === 'microsoft' ? (
              <Navigate to="/admin" replace />
            ) : isLoggedIn && loginType === 'normal' ? (
              <Navigate to="/home" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          } />

          {/* Public routes */}
          <Route path="/home" element={<Homepage />} />
          <Route path="/registerpage" element={<RegisterPage />} />
          <Route path="/survey/:id?" element={<Survey />} />
          <Route path="/passwordReset" element={<PasswordReset />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />

          {/* Admin route — only SSO/Microsoft users allowed */}
          <Route path="/admin" element={
            loginType === 'normal' ? <Navigate to="/home" replace /> : <AdminRoute />
          } />

          {/* Login route with guard */}
          <Route
            path="/login"
            element={
              isLoggedIn && loginType === 'microsoft' ? (
                <Navigate to="/admin" replace />
              ) : isLoggedIn && loginType === 'normal' ? (
                <Navigate to="/home" replace />
              ) : (
                <React.Suspense fallback={<Loading message="Loading..." />}>
                  <LoginSignUpRoute setIsLoggedIn={setIsLoggedIn} />
                </React.Suspense>
              )
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

export default App;